import { useState, useEffect, useCallback, useRef } from 'react';
import cacheStore, {
  TTL, subscribe as subscribeCache, isCacheEnabled, checkDataVersion, isVersionFresh,
} from './cacheStore';
import {
  isOnline,
  reportNetworkFailure,
  isNetworkError,
  subscribeNetwork,
} from './useNetworkStatus';

/**
 * useCachedData — stale-while-revalidate drop-in for app/useData.
 *
 * ---------------------------------------------------------------------
 * DROP-IN CONTRACT
 * ---------------------------------------------------------------------
 * Called exactly like the hook it replaces:
 *
 *     const { data, loading, error, reload } = useCachedData(loader, deps);
 *
 * and it returns everything useData returned — { data, loading, error,
 * reload, setData } — with identical semantics. A page that changes
 * nothing but the import keeps working, because WITHOUT A KEY THIS HOOK
 * DOES NOT CACHE. That is deliberate:
 *
 *   A cache key derived silently from `deps` would be wrong in the one
 *   way that matters. Look at the real call sites:
 *
 *       useData(() => Promise.all([api.studentDirectory({ date }),
 *                                  api.sections()]), [date])
 *       useData(async () => { ... api.students() ... }, [])
 *
 *   Both have deps that describe only *part* of what the loader fetches,
 *   and two different pages both have `deps: []`. Auto-keying on deps
 *   alone would collide those two `[]` loaders onto one cache entry and
 *   serve the Overview payload to the Students page. So the key is the
 *   author's explicit statement of what this request IS, and the absence
 *   of one means "don't cache" rather than "guess".
 *
 * To turn caching on, pass a third argument:
 *
 *     const { data, loading, error, reload, revalidating, fromCache, age }
 *       = useCachedData(
 *           () => Promise.all([api.studentDirectory({ date }), api.sections()])
 *                   .then(([dir, sections]) => ({ dir, sections })),
 *           [date],
 *           { key: 'student-directory', ttl: TTL.VOLATILE }
 *         );
 *
 * `deps` are appended to the key automatically, so `include_inactive`,
 * `date`, `student_id` and every other query param that is already in
 * the deps array becomes part of the identity of the entry. That is the
 * fix for today's new params: as long as the value is in `deps` (which
 * it must be anyway, or the loader would not re-run), it is in the key.
 *
 * ---------------------------------------------------------------------
 * BEHAVIOUR (stale-while-revalidate)
 * ---------------------------------------------------------------------
 *   1. Mount with a cached entry (fresh or stale) -> paint it at 0ms,
 *      `loading` is false immediately, `fromCache` is true.
 *   2. If the entry was stale (or `revalidate` is forced), a background
 *      fetch starts. `revalidating` is true while it runs. Nothing
 *      flickers: the old data stays on screen.
 *   3. When it lands, data is replaced and `fromCache` goes false.
 *   4. No cached entry -> exactly the old behaviour, a normal spinner.
 *   5. The fetch fails while cached data is on screen -> the data STAYS
 *      and `error` stays null; `revalidateError` carries the reason. A
 *      background refresh failing must never blank a working page.
 *   6. Offline with a cached entry -> serve it at any age, set
 *      `offline` and `age` so the page can say how old it is.
 *   7. Live-only mode (navbar toggle) -> never read, never write. The
 *      hook behaves precisely like useData.
 *
 * MUTATIONS ARE NEVER CACHED. This hook only ever runs a loader that the
 * page passes for *reading*, and cacheStore additionally refuses any key
 * matching an auth endpoint. After a POST/PATCH/DELETE a page calls
 * `invalidate('students')` and then `reload()`; see the header comment
 * on cacheStore.invalidate.
 */

/**
 * Build the storage key. Deps are serialised so that
 * `['students', 2026]` and `['students', '2026']` do not collide, and so
 * that an object dep (a params bag) contributes its contents.
 *
 * JSON.stringify is used rather than String() because a params object
 * like `{ include_inactive: 1 }` stringifies to '[object Object]'
 * otherwise, which would silently merge the with-inactive and
 * without-inactive variants of /students into ONE entry — exactly the
 * bug the new query params introduce.
 */
function buildKey(key, deps) {
  if (!key) return null;
  if (!deps || deps.length === 0) return key;
  let suffix;
  try {
    suffix = JSON.stringify(deps);
  } catch {
    // Non-serialisable dep (a function, a DOM node, a circular object).
    // We cannot prove two renders mean the same request, so we refuse to
    // cache rather than risk serving the wrong entry.
    return null;
  }
  // A dep that is undefined/null serialises to null; that is fine and
  // distinct from the value being absent from the array.
  return `${key}:${suffix}`;
}

/** How old, in Arabic, for the "showing data from…" line. */
export function formatAge(ms) {
  if (ms == null) return '';
  const s = Math.max(0, Math.round(ms / 1000));
  if (s < 60) return 'قبل ثوانٍ';
  const m = Math.round(s / 60);
  if (m < 60) return `قبل ${m} دقيقة`;
  const h = Math.round(m / 60);
  if (h < 24) return `قبل ${h} ساعة`;
  const d = Math.round(h / 24);
  return `قبل ${d} يوم`;
}

/**
 * @param {Function} loader          async () => data. Same as useData.
 * @param {Array}    deps            re-run triggers. Same as useData.
 * @param {object}  [options]
 * @param {string}  [options.key]    REQUIRED to enable caching. A stable
 *                                   noun for this request, e.g.
 *                                   'students-page', 'sections'.
 * @param {number}  [options.ttl]    ms before the entry is considered
 *                                   stale. Defaults to TTL.ROSTER (5m).
 *                                   Use the TTL catalogue, not literals.
 * @param {boolean} [options.persist] also write to localStorage so the
 *                                   entry survives a browser restart.
 *                                   Only for small, slow-moving data.
 * @param {boolean} [options.enabled=true] set false to skip loading
 *                                   entirely (e.g. a modal's tab that
 *                                   has not been opened).
 *
 * @returns {{
 *   data: *,                  // the payload, or null
 *   loading: boolean,         // true only when there is nothing to show
 *   error: string|null,       // fatal: no data AND the fetch failed
 *   reload: Function,         // force a fresh fetch, bypassing the cache
 *   setData: Function,        // optimistic local edit, same as useData
 *   revalidating: boolean,    // a background refresh is in flight
 *   fromCache: boolean,       // what is on screen came from the cache
 *   age: number|null,         // ms since the shown data was fetched
 *   ageLabel: string,         // that age, in Arabic
 *   updatedAt: number|null,   // Date.now() of the shown data
 *   offline: boolean,         // we could not reach the server
 *   revalidateError: string|null, // background refresh failed, data kept
 *   invalidate: Function      // drop THIS entry, then reload()
 * }}
 */
export default function useCachedData(loader, deps = [], options = {}) {
  const {
    key = null,
    ttl = TTL.ROSTER,
    persist = false,
    enabled = true,
  } = options;

  const cacheKey = buildKey(key, deps);

  // The loader changes identity on every render (it is an inline arrow
  // at every call site). Keeping it in a ref and driving the effect off
  // `deps` is exactly what useData did, and is why the eslint disable
  // below is correct rather than a papering-over.
  const loaderRef = useRef(loader);
  loaderRef.current = loader;

  // Seed synchronously from the cache. Doing this in useState's
  // initialiser rather than in an effect is what makes "0ms" literally
  // true: the very first paint already has the rows. An effect would
  // paint an empty state for one frame first.
  //
  // ...but only when the cache is KNOWN to match the server (its data
  // version was confirmed seconds ago), or when we are offline and the
  // cache is all there is. Otherwise run() checks the version first:
  // painting a list that may be out of date and then swapping it is
  // exactly what must not happen after someone changed the data.
  const seed = () => {
    if (!enabled || !cacheKey || !isCacheEnabled()) return null;
    if (isOnline() && !isVersionFresh()) return null;
    try { return cacheStore.peek(cacheKey); } catch { return null; }
  };

  const [state, setState] = useState(() => {
    const hit = seed();
    return {
      data: hit ? hit.data : null,
      loading: !hit && enabled,
      error: null,
      fromCache: !!hit,
      updatedAt: hit ? hit.at : null,
      revalidating: false,
      revalidateError: null,
    };
  });

  const [offline, setOffline] = useState(!isOnline());

  useEffect(() => subscribeNetwork(() => setOffline(!isOnline())), []);

  // `run` needs to know whether anything is currently on screen (to
  // decide fatal vs non-fatal failure) WITHOUT taking `state` as a
  // dependency — that would rebuild the callback on every fetch and
  // re-trigger the effect that calls it, i.e. an infinite loop. A ref
  // gives the current value with no identity change.
  const dataRef = useRef(state.data);
  dataRef.current = state.data;

  // Guards a slow request resolving after a newer one. Same technique as
  // useData, but it must also survive the extra async hop SWR adds.
  const runId = useRef(0);
  // True between mount and unmount; prevents setState after unmount when
  // a background revalidation outlives the page.
  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    return () => { alive.current = false; };
  }, []);

  /**
   * The single fetch path.
   * @param {object} o
   * @param {boolean} o.background true when data is already on screen,
   *        so failures must be non-fatal and no spinner is shown.
   * @param {boolean} o.skipCacheRead true for reload(): an explicit
   *        refresh must always hit the network.
   */
  const run = useCallback(async ({ background = false, skipCacheRead = false } = {}) => {
    if (!enabled) return;

    const id = ++runId.current;
    const usingCache = isCacheEnabled() && !!cacheKey;

    // ---- 0. has the database moved since this was cached? ------------
    // A changed version drops the whole cache (cacheStore.noteDataVersion),
    // so the peek below then misses and we go to the network. Offline
    // the check just fails and the cached copy is used.
    if (usingCache && !skipCacheRead && isOnline()) {
      await checkDataVersion();
      if (!alive.current || id !== runId.current) return;
    }

    // ---- 1. try to paint something instantly -------------------------
    let hit = null;
    if (usingCache && !skipCacheRead) {
      try { hit = cacheStore.peek(cacheKey); } catch { hit = null; }
    }

    if (hit && !background) {
      setState((s) => ({
        ...s,
        data: hit.data,
        loading: false,
        error: null,
        fromCache: true,
        updatedAt: hit.at,
      }));
    }

    // A fresh (non-stale) hit needs no network call at all. This is the
    // case that makes tab-switching free.
    if (hit && !hit.stale && !skipCacheRead) {
      if (alive.current && id === runId.current) {
        setState((s) => ({ ...s, revalidating: false, revalidateError: null }));
      }
      return;
    }

    // ---- 2. offline: serve whatever we have, do not error ------------
    if (!isOnline() && hit) {
      if (alive.current && id === runId.current) {
        setState((s) => ({
          ...s,
          data: hit.data,
          loading: false,
          error: null,
          fromCache: true,
          updatedAt: hit.at,
          revalidating: false,
        }));
      }
      return;
    }

    // ---- 3. fetch ----------------------------------------------------
    // `showingSomething` decides whether a failure is fatal. It is
    // computed from the hit AND from data already in state, because a
    // reload() on a page that has data must not blank it either.
    const hadData = !!hit || dataRef.current != null;

    if (alive.current && id === runId.current) {
      setState((s) => ({
        ...s,
        loading: !hadData,
        revalidating: hadData,
        error: hadData ? null : s.error,
        revalidateError: null,
      }));
    }

    try {
      const result = await loaderRef.current();
      if (!alive.current || id !== runId.current) return;

      // Connectivity is reported by ui/api.js itself: a loader can succeed
      // OFFLINE (answered from ui/offline/snapshotStore), so success here
      // is not proof that the network is back.

      // Write AFTER a success only. A failed request must never poison
      // the cache, and a mutation never reaches this line because this
      // hook is only ever handed read loaders.
      if (usingCache && result !== undefined) {
        try { cacheStore.set(cacheKey, result, { ttl, persist }); } catch { /* cache is best-effort */ }
      }

      setState({
        data: result,
        loading: false,
        error: null,
        fromCache: false,
        updatedAt: Date.now(),
        revalidating: false,
        revalidateError: null,
      });
    } catch (err) {
      if (!alive.current || id !== runId.current) return;

      const message = err?.message || 'فشل تحميل البيانات';

      // Only a transport failure means "offline". A 403/404/500 came
      // back from a reachable server and must not flip the navbar dot.
      if (isNetworkError(err)) reportNetworkFailure();

      setState((s) => {
        const showing = s.data != null;
        return showing
          // Non-fatal: keep the rows, report it quietly.
          ? { ...s, loading: false, revalidating: false, revalidateError: message }
          // Fatal: there is nothing on screen, this is the old behaviour.
          : { ...s, loading: false, revalidating: false, error: message, revalidateError: null };
      });
    }
    // `deps` is spread so this hook re-runs on exactly the same triggers
    // useData did. The other entries are stable primitives.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cacheKey, ttl, persist, enabled, ...deps]);

  useEffect(() => {
    if (!enabled) return;
    run();
  }, [run, enabled]);

  // Re-run when the navbar toggle flips. Turning caching OFF must
  // immediately refetch live data rather than leaving the stale copy the
  // user just said they did not trust on screen.
  useEffect(() => subscribeCache(() => {
    if (!alive.current || !enabled) return;
    run({ background: true, skipCacheRead: !isCacheEnabled() });
  }), [run, enabled]);

  // Came back online with cached data on screen -> refresh it in the
  // background. Deliberately keyed on `offline` ALONE: this must fire on
  // the false->true transition of connectivity, not every time
  // `fromCache` changes (which would re-fetch immediately after every
  // cache hit). `fromCacheRef` supplies the current value without
  // widening the dependency array.
  const fromCacheRef = useRef(state.fromCache);
  fromCacheRef.current = state.fromCache;

  const wasOffline = useRef(offline);
  useEffect(() => {
    const recovered = wasOffline.current && !offline;
    wasOffline.current = offline;
    if (recovered && enabled && fromCacheRef.current) {
      run({ background: true, skipCacheRead: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offline, enabled]);

  /** Explicit refresh. Always hits the network, never reads the cache.
   *  This is what a page calls after a successful save. */
  const reload = useCallback(() => run({ skipCacheRead: true }), [run]);

  /** Drop this entry and refetch. Use when a page knows its own data is
   *  now wrong but does not want to reason about key patterns. */
  const invalidateSelf = useCallback(() => {
    if (cacheKey) { try { cacheStore.remove(cacheKey); } catch { /* ignore */ } }
    return run({ skipCacheRead: true });
  }, [cacheKey, run]);

  /** Optimistic local edit. Identical to useData's setData, but it also
   *  writes the edited value back to the cache so a tab switch does not
   *  resurrect the pre-edit copy. */
  const setData = useCallback((next) => {
    setState((s) => {
      const value = typeof next === 'function' ? next(s.data) : next;
      if (isCacheEnabled() && cacheKey && value !== undefined) {
        try { cacheStore.set(cacheKey, value, { ttl, persist }); } catch { /* ignore */ }
      }
      return { ...s, data: value };
    });
  }, [cacheKey, ttl, persist]);

  const age = state.updatedAt ? Date.now() - state.updatedAt : null;

  return {
    // --- useData-compatible surface ---
    data: state.data,
    loading: state.loading,
    error: state.error,
    reload,
    setData,
    // --- cache extras ---
    revalidating: state.revalidating,
    revalidateError: state.revalidateError,
    fromCache: state.fromCache,
    updatedAt: state.updatedAt,
    age,
    ageLabel: formatAge(age),
    offline,
    invalidate: invalidateSelf,
  };
}

export { TTL };
